import { useEffect, useState } from "react";
import api, { qs } from "@/lib/api";
import { monthOptions, monthLabel, pctTone, fmtPct } from "@/lib/dpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { FleetAwardCard } from "@/components/overview/FleetAwardCard";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";
export const TIER_META = {
  gold: { label: "Gold", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: "text-amber-500" },
  silver: { label: "Silver", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: "text-slate-400" },
  bronze: { label: "Bronze", cls: "bg-orange-100 text-orange-800 border-orange-300", icon: "text-orange-700" },
};

export default function DpcLeague() {
  const [month, setMonth] = useState(monthOptions()[1]);
  const [data, setData] = useState(null);
  const [board, setBoard] = useState("dpi");

  useEffect(() => {
    api.get(`/dpc/leaderboard?${qs({ month })}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [month]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading leaderboards…</div>;
  const active = data.boards.find((b) => b.key === board);
  const isFleet = active?.kind === "fleet";
  const isPct = board !== "dpi" && !isFleet;
  const boardEmpty = active.rows.every((r) => r.rank == null && r.value == null);

  return (
    <div className="space-y-4" data-testid="dpc-league">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">DPC Leaderboards <span className="text-muted-foreground font-medium text-base">· {monthLabel(month)}</span></h1>
          <p className="text-xs text-muted-foreground mt-1" data-testid="dpc-league-eligibility">{isFleet ? `${active.eligibility} · ${active.definition}` : data.eligibility} · Tiers: top 10% Gold, 20% Silver, 30% Bronze{data.prev_month ? ` · Δ vs ${monthLabel(data.prev_month)}` : ""}</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="dpc-league-month"><SelectValue /></SelectTrigger>
          <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-1 text-xs">
        {data.boards.map((b) => (
          <button key={b.key} onClick={() => setBoard(b.key)} data-testid={`dpc-board-${b.key}`}
            className={`px-3 py-1.5 rounded-md border font-medium transition-colors duration-200 ${board === b.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}>{b.label}</button>
        ))}
      </div>

      {data.masked_view && <p className="text-[11px] text-muted-foreground">You see full numbers for your own dealer; others show rank &amp; tier only.</p>}

      {isFleet && (data.td_award
        ? <FleetAwardCard a={data.td_award} />
        : <p className="text-[11px] text-muted-foreground" data-testid="td-award-none">No award card yet — the card is issued to the first dealer with TD cars set to reach 20 test drives in the month, and updates daily until the month closes.</p>)}

      <div className="border bg-card rounded-md overflow-auto max-h-[65vh]">
        {boardEmpty ? (
          <p className="text-sm text-muted-foreground text-center py-12" data-testid="dpc-board-empty">
            {board === "compliance" ? "Compliance ranks appear from day 2 of the month (based on completed upload days)." : isFleet ? "No dealer qualifies yet — ranks appear once a dealer with TD cars set logs 20 test drives in the month." : "No data yet for this board — ranks appear once plans and achievements are in."}
          </p>
        ) : (
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card"><tr>{["#", "Dealer", active?.key === "dpi" ? "DPI (0–100)" : isFleet ? "TDs / car / day" : "Value", ...(isFleet ? ["TD cars", "Test drives"] : []), "Δ", "Tier"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {active.rows.map((r) => {
              const tm = TIER_META[r.tier];
              return (
                <tr key={r.dealer_code} className={`border-b last:border-0 hover:bg-muted/50 ${!r.eligible ? "opacity-50" : ""}`} data-testid={`league-row-${r.dealer_code}`}>
                  <td className="px-3 py-1.5 num font-bold w-10">{r.rank ?? "—"}</td>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">
                    {r.dealer_code} <span className="font-normal text-muted-foreground">{r.dealer_name}</span>
                    {!r.eligible && !isFleet && <span className="ml-1.5 text-[10px] text-muted-foreground">(retail plan &lt; 5)</span>}
                    {!r.eligible && isFleet && <span className="ml-1.5 text-[10px] text-muted-foreground" data-testid={`league-td-inel-${r.dealer_code}`}>{r.needs_td_cars ? "(TD cars not set)" : `(${r.test_drives ?? 0} test drives · needs 20)`}</span>}
                  </td>
                  <td className={`px-3 py-1.5 num font-bold ${isPct ? pctTone(r.value) : ""}`}>
                    {r.masked ? "•••" : r.value == null ? "—" : isPct ? fmtPct(r.value) : isFleet ? Number(r.value).toFixed(2) : r.value}
                  </td>
                  {isFleet && <td className="px-3 py-1.5 num">{r.masked ? "•••" : r.td_cars ?? "—"}</td>}
                  {isFleet && <td className="px-3 py-1.5 num">{r.masked ? "•••" : r.test_drives ?? "—"}</td>}
                  <td className="px-3 py-1.5">
                    {r.change == null ? <Minus size={12} className="text-muted-foreground" /> :
                      r.change > 0 ? <span className="flex items-center gap-0.5 text-emerald-700 text-xs font-bold"><ArrowUp size={12} />{r.change}</span> :
                      r.change < 0 ? <span className="flex items-center gap-0.5 text-red-600 text-xs font-bold"><ArrowDown size={12} />{-r.change}</span> :
                      <Minus size={12} className="text-muted-foreground" />}
                  </td>
                  <td className="px-3 py-1.5">
                    {tm && <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-bold ${tm.cls}`}><Trophy size={10} className={tm.icon} />{tm.label}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
